import { Button } from '@storeblocks/button';
import { CheckboxProps } from '@storeblocks/checkbox-atom';
import { Checkboxes } from '@storeblocks/checkboxes';
import { Modal } from '@storeblocks/modal';
import { type FC } from 'react';
import { useDispatch } from 'react-redux';

import { Permission } from '@/api/permissions/models/Permission';
import {
  permissionsApi,
  usePermissionsQuery,
} from '@/api/permissions/permissionsApi';
import { AppDispatch } from '@/store';
import { convertStringToEnum } from '@/util/convertStringToEnum';

import { WithGap } from '../WithGap';

interface Props {
  show: boolean;
  onClose: () => void;
}

export const RoleManager: FC<Props> = ({ show, onClose }: Props) => {
  const { data: permissions } = usePermissionsQuery();
  const dispatch = useDispatch<AppDispatch>();

  const updatePermissions = (permissions: Permission[]): void => {
    const updatePermissionsAction = permissionsApi.util.upsertQueryData(
      'permissions',
      undefined,
      permissions,
    );

    dispatch(updatePermissionsAction);
  };

  const options: CheckboxProps[] = Object.values(Permission).map(
    (permission) => ({
      id: permission,
      value: permission,
      label: permission,
      checked: permissions?.includes(permission) ?? false,
    }),
  );

  return (
    <Modal
      open={show}
      onClose={onClose}
      trigger={undefined}
      data-test="role-manager-modal"
      content={
        <WithGap>
          <p className="font-body">
            Note: the components will not rerender automatically when changing
            the permissions. Navigate to a new page in the application to make
            the components rerender.
          </p>

          <Checkboxes
            groupName="development-tools-role-manager"
            options={options}
            onChange={(e) => {
              const permission = convertStringToEnum(
                e.target.value,
                Permission,
              );
              let checkedPermissions = options
                .filter((option) => option.checked)
                .map((option) => option.value);
              if (e.target.checked) {
                checkedPermissions.push(permission);
              } else {
                checkedPermissions = checkedPermissions.filter(
                  (option) => option !== permission,
                );
              }

              updatePermissions(checkedPermissions as Permission[]);
            }}
          />
        </WithGap>
      }
      title="Role Manager"
      footer={
        <Button onClick={onClose} key="close-button">
          Close
        </Button>
      }
    ></Modal>
  );
};
